import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Checkbox, Field, Modal, Segments, Switch, UploadIcon, useToast } from "@signet/ui";
import { parseAccountsText, type ParsedFileLine } from "../../shared/parse.ts";
import { stockKeys, uploadStock, type AccountType } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Upload accounts: a supplier's .txt, its listing or order link (required),
   and aged or soft reg. The file is read here in the browser with the same
   rules the server would use (shared/parse.ts), and every line is shown
   before anything is saved: lines the reader is unsure of are highlighted
   and can be fixed by hand.
   ────────────────────────────────────────────────────────────────────────── */

type Row = ParsedFileLine & { include: boolean };

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

export function UploadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [filename, setFilename] = useState<string | null>(null);
  const [link, setLink] = useState("");
  const [accountType, setAccountType] = useState<AccountType | "">("");
  const [show, setShow] = useState(false);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const client = useQueryClient();
  const toast = useToast();

  const reset = () => {
    setRows([]);
    setFilename(null);
    setLink("");
    setAccountType("");
    setShow(false);
  };
  const close = () => {
    reset();
    onClose();
  };

  const read = async (file: File | undefined) => {
    if (!file) return;
    const text = await file.text();
    setFilename(file.name);
    setRows(parseAccountsText(text).map((row) => ({ ...row, include: Boolean(row.username) && !row.duplicate })));
  };

  const edit = (index: number, patch: Partial<Row>) =>
    setRows((all) => all.map((row, i) => (i === index ? { ...row, ...patch, unsure: false } : row)));

  const chosen = rows.filter((row) => row.include && row.username);
  const linkOk = /^https?:\/\/\S+\.\S+/i.test(link.trim());
  const ready = chosen.length > 0 && linkOk && accountType !== "";

  const save = useMutation({
    mutationFn: () =>
      uploadStock({
        link: link.trim(),
        accountType: accountType as AccountType,
        filename,
        rows: chosen.map((row) => ({
          username: row.username!, password: row.password, email: row.email, emailPassword: row.emailPassword,
          backupEmail: row.backupEmail, backupEmailPassword: row.backupEmailPassword, twoFactor: row.twoFactor, other: row.other,
        })),
      }),
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: stockKeys.all });
      toast.success(
        `${result.added} ${result.added === 1 ? "account" : "accounts"} added`,
        result.skipped.length ? `${result.skipped.length} already in Signet and skipped.` : "Each has its setup and bio. Posters can take them now.",
      );
      close();
    },
    onError: (error) => toast.error("Couldn't upload them", messageOf(error)),
  });

  const unsure = rows.filter((row) => row.unsure || !row.username).length;
  const masked = (value: string | null) => (value === null ? "" : show ? value : "•".repeat(Math.min(10, value.length)));

  return (
    <Modal
      open={open}
      onClose={close}
      size="huge"
      title="Upload accounts"
      description="A supplier's .txt, one account a line. Each one gets its setup and a bio as it is added."
      footer={
        <>
          <button type="button" className="secondary-button" onClick={close}>Cancel</button>
          <button type="button" className="button brass" disabled={!ready || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Uploading…" : chosen.length ? `Upload ${chosen.length} ${chosen.length === 1 ? "account" : "accounts"}` : "Upload"}
          </button>
        </>
      }
    >
      <div className="igs-upload">
        <div className="igs-upload-fields">
          <Field label="Account link" hint="The supplier's listing or order page. Required." error={link && !linkOk ? "Paste the full address, starting with https://" : undefined}>
            <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://accsmarket.com/en/item/…" />
          </Field>
          <Field label="Account type">
            <Segments<AccountType | "">
              label="Account type"
              value={accountType}
              options={[{ value: "aged", label: "Aged" }, { value: "soft_reg", label: "Soft reg" }]}
              onChange={setAccountType}
            />
          </Field>
        </div>

        <div
          className={`igs-drop${dragging ? " over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void read(e.dataTransfer.files[0]);
          }}
        >
          <UploadIcon size={18} />
          <div>
            <strong>{filename ?? "Drop the .txt here"}</strong>
            <small>{filename ? `${rows.length} lines read` : "or choose it from your computer"}</small>
          </div>
          <button type="button" className="secondary-button compact" onClick={() => input.current?.click()}>
            {filename ? "Choose another" : "Choose file"}
          </button>
          <input
            ref={input}
            type="file"
            accept=".txt,text/plain"
            hidden
            onChange={(e) => {
              void read(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </div>

        {rows.length ? (
          <>
            <div className="igs-upload-summary">
              <span>{chosen.length} of {rows.length} will be added.</span>
              {unsure ? <span className="igs-warn-text">{unsure} {unsure === 1 ? "line needs" : "lines need"} a look: highlighted below.</span> : <span>Every line read cleanly.</span>}
              <label className="igs-switch">
                <Switch checked={show} onChange={setShow} label="Show passwords" /> Show passwords
              </label>
            </div>
            <div className="table-scroll igs-upload-table">
              <div className="table" role="table" aria-label="Accounts in the file">
                <div className="table-row head" role="row">
                  <span role="columnheader">Add</span>
                  <span role="columnheader">Line</span>
                  <span role="columnheader">Username</span>
                  <span role="columnheader">Password</span>
                  <span role="columnheader">Email</span>
                  <span role="columnheader">Email password</span>
                  <span role="columnheader">2FA</span>
                  <span role="columnheader">Other</span>
                </div>
                {rows.map((row, i) => {
                  const flag = row.unsure || !row.username || row.duplicate;
                  return (
                    <div key={row.line} role="row" className={`table-row${flag ? " igs-unsure" : ""}${row.include ? "" : " igs-off"}`}>
                      <span role="cell">
                        <Checkbox checked={row.include} disabled={!row.username} onChange={(include) => edit(i, { include })} label={`Add line ${row.line}`} />
                      </span>
                      <span role="cell" className="faint tabular">{row.line}</span>
                      {flag ? (
                        <>
                          <span role="cell"><input className="igs-cell-input" value={row.username ?? ""} onChange={(e) => edit(i, { username: e.target.value.trim().replace(/^@/, "").toLowerCase() || null, include: Boolean(e.target.value.trim()) })} placeholder="username" /></span>
                          <span role="cell"><input className="igs-cell-input" value={row.password ?? ""} onChange={(e) => edit(i, { password: e.target.value || null })} placeholder="password" /></span>
                          <span role="cell"><input className="igs-cell-input" value={row.email ?? ""} onChange={(e) => edit(i, { email: e.target.value || null })} placeholder="email" /></span>
                          <span role="cell"><input className="igs-cell-input" value={row.emailPassword ?? ""} onChange={(e) => edit(i, { emailPassword: e.target.value || null })} placeholder="email password" /></span>
                          <span role="cell"><input className="igs-cell-input" value={row.twoFactor ?? ""} onChange={(e) => edit(i, { twoFactor: e.target.value.replace(/\s+/g, "").toUpperCase() || null })} placeholder="2FA key" /></span>
                        </>
                      ) : (
                        <>
                          <span role="cell" className="truncate">@{row.username}</span>
                          <span role="cell" className="truncate tabular">{masked(row.password)}</span>
                          <span role="cell" className="truncate">{row.email ?? ""}</span>
                          <span role="cell" className="truncate tabular">{masked(row.emailPassword)}</span>
                          <span role="cell">{row.twoFactor ? "Yes" : ""}</span>
                        </>
                      )}
                      <span role="cell" className="truncate faint">
                        {row.duplicate ? "Repeated in the file" : row.other.length ? masked(row.other.join(" : ")) : row.backupEmail ? `Backup ${row.backupEmail}` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
